import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { WidgetApp } from './App';
import { PreviewApp } from './components/PreviewApp';
import { CanvasUpsellApp } from './components/CanvasUpsellApp';
import { installProtection } from './protection';
import type {
  CanvasMountData,
  InputSetCode,
  PreviewMountData,
  PreviewProduct,
  ShopifyVariant,
  UiConfig,
  WidgetMountData,
} from './types';

const UPLOAD_ROOT_SELECTOR = '#bf-widget-root';
const PREVIEW_ROOT_SELECTOR = '#bf-preview-root';
const CANVAS_ROOT_SELECTOR = '#bf-canvas-root';

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    // Liquid emits the literal `null` when a metafield is unset — treat that
    // as "no data" and use the fallback instead of returning null to callers.
    return (parsed ?? fallback) as T;
  } catch (e) {
    console.warn('[bf-widget] invalid JSON in data-* attribute, using fallback', e);
    return fallback;
  }
}

function readMountData(el: HTMLElement): WidgetMountData {
  return {
    productId: el.dataset.productId ?? '',
    inputSetCode: (el.dataset.inputSetCode || 'default') as InputSetCode,
    uiConfig: parseJson<UiConfig>(el.dataset.uiConfig, { style_samples: [] }),
    variants: parseJson<ShopifyVariant[]>(el.dataset.variants, []),
    previewPage: el.dataset.previewPage || '/pages/preview',
  };
}

function readPreviewMountData(el: HTMLElement): PreviewMountData {
  return {
    products: parseJson<Record<string, PreviewProduct>>(el.dataset.products, {}),
  };
}

function readCanvasMountData(el: HTMLElement): CanvasMountData {
  const uiConfig = parseJson<UiConfig>(el.dataset.uiConfig, { style_samples: [] });
  return {
    productId: el.dataset.productId ?? '',
    canvasHandle: el.dataset.canvasHandle ?? '',
    variants: parseJson<ShopifyVariant[]>(el.dataset.variants, []),
    moments: uiConfig.moments ?? [],
  };
}

function mountUpload(el: HTMLElement): void {
  el.classList.add('bf-widget');
  const data = readMountData(el);

  // NOTE: no manual fbq('ViewContent') here. The ai-landing template is a
  // Shopify product page, so Shopify's native Meta pixel already fires
  // ViewContent (with a `sh-` eventID + CAPI dedup). A manual call would
  // duplicate it with no eventID to dedupe against.

  createRoot(el).render(
    <StrictMode>
      <WidgetApp data={data} view="upload" />
    </StrictMode>,
  );
}

function mountPreview(el: HTMLElement): void {
  el.classList.add('bf-widget');
  const data = readPreviewMountData(el);
  createRoot(el).render(
    <StrictMode>
      <PreviewApp data={data} />
    </StrictMode>,
  );
}

function mountCanvas(el: HTMLElement): void {
  el.classList.add('bf-widget');
  const data = readCanvasMountData(el);
  createRoot(el).render(
    <StrictMode>
      <CanvasUpsellApp data={data} />
    </StrictMode>,
  );
}

function boot(): void {
  // Image protection (right-click guard always; F12/DevTools guard only when
  // data-disable-f12="true" is set on a mount root). Matches WooCommerce.
  const root =
    document.querySelector<HTMLElement>(UPLOAD_ROOT_SELECTOR) ||
    document.querySelector<HTMLElement>(PREVIEW_ROOT_SELECTOR) ||
    document.querySelector<HTMLElement>(CANVAS_ROOT_SELECTOR);
  const disableF12 = root?.dataset.disableF12 === 'true';
  installProtection(disableF12);

  document.querySelectorAll<HTMLElement>(UPLOAD_ROOT_SELECTOR).forEach(mountUpload);
  document.querySelectorAll<HTMLElement>(PREVIEW_ROOT_SELECTOR).forEach(mountPreview);
  document.querySelectorAll<HTMLElement>(CANVAS_ROOT_SELECTOR).forEach(mountCanvas);
}

// Script is loaded `defer` from theme.liquid, so DOM is parsed by the time
// this runs. If pages without a mount target load the bundle (homepage, etc.),
// boot() finds nothing and exits cleanly — no crash.
boot();
